import DataTable, { type DataTableColumn } from '@/components/ui/DataTable'

// ─── Types ──────────────────────────────────────

export interface VpoProduct {
  id: number
  upc: string | null
  style_no: string | null
  style_descr: string | null
  color: string | null
  size: string | null
  cost_price: number | string | null
  selling_price: number | string | null
  ordered: number
  confirmed: number
  invoiced: number
  received: number
  bo: number
  ordered_tot_cost: number | string | null
  confirmed_tot_cost: number | string | null
  invoiced_tot_cost: number | string | null
  received_tot_cost: number | string | null
  bo_tot_cost: number | string | null
}

interface ProductsTabProps {
  products: VpoProduct[]
  currency: string | null
}

// ─── Helpers ────────────────────────────────────

function money(value: number | string | null, currency: string | null): string {
  if (value == null) return '—'
  const n = typeof value === 'string' ? parseFloat(value) : value
  if (isNaN(n) || n === 0) return '—'
  const symbol = currency === 'eur' ? '€' : currency === 'chf' ? 'CHF ' : '$'
  return `${symbol}${n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
}

function sumNum(rows: VpoProduct[], pick: (r: VpoProduct) => number | string | null): number {
  return rows.reduce((acc, r) => {
    const v = pick(r)
    const n = v == null ? 0 : (typeof v === 'string' ? parseFloat(v) : v)
    return acc + (isNaN(n) ? 0 : n)
  }, 0)
}

// ─── Component ──────────────────────────────────

export default function ProductsTab({ products, currency }: ProductsTabProps) {
  const columns: DataTableColumn<VpoProduct>[] = [
    {
      key: 'upc', label: 'UPC',
      render: r => <span className="font-mono text-xs">{r.upc || '—'}</span>,
      sortValue: r => r.upc || '', filterValue: r => r.upc || '',
    },
    {
      key: 'style_no', label: 'Style #',
      render: r => <>{r.style_no || '—'}</>,
      sortValue: r => r.style_no || '', filterValue: r => r.style_no || '',
    },
    {
      key: 'style_descr', label: 'Description',
      render: r => <span className="text-xs">{r.style_descr || '—'}</span>,
      filterValue: r => r.style_descr || '',
    },
    {
      key: 'color', label: 'Color',
      render: r => <>{r.color || '—'}</>,
      filterValue: r => r.color || '',
    },
    {
      key: 'size', label: 'Size',
      render: r => <>{r.size || '—'}</>,
      filterValue: r => r.size || '',
    },
    {
      key: 'cost_price', label: 'Cost', align: 'right',
      render: r => <span className="tabular-nums">{money(r.cost_price, currency)}</span>,
      sortValue: r => Number(r.cost_price ?? 0),
    },
    {
      key: 'ordered', label: 'Ordered', align: 'right',
      render: r => <span className="tabular-nums">{r.ordered}</span>,
      sortValue: r => r.ordered,
    },
    {
      key: 'confirmed', label: 'Confirmed', align: 'right',
      render: r => <span className="tabular-nums">{r.confirmed}</span>,
      sortValue: r => r.confirmed,
    },
    {
      key: 'invoiced', label: 'Invoiced', align: 'right',
      render: r => <span className="tabular-nums">{r.invoiced}</span>,
      sortValue: r => r.invoiced,
    },
    {
      key: 'received', label: 'Received', align: 'right',
      render: r => <span className="tabular-nums">{r.received}</span>,
      sortValue: r => r.received,
    },
    {
      key: 'bo', label: 'BO', align: 'right',
      render: r => <span className="tabular-nums">{r.bo}</span>,
      sortValue: r => r.bo,
    },
    {
      key: 'ordered_tot_cost', label: 'Tot. Cost', align: 'right',
      render: r => <span className="tabular-nums font-medium">{money(r.ordered_tot_cost, currency)}</span>,
      sortValue: r => Number(r.ordered_tot_cost ?? 0),
    },
  ]

  // Aggregate summary across all rows shown — drives a single footer row.
  // DataTable doesn't yet have a built-in footer slot, so render it just
  // below the table for now.
  const totals = {
    ordered:   products.reduce((s, r) => s + r.ordered, 0),
    confirmed: products.reduce((s, r) => s + r.confirmed, 0),
    invoiced:  products.reduce((s, r) => s + r.invoiced, 0),
    received:  products.reduce((s, r) => s + r.received, 0),
    bo:        products.reduce((s, r) => s + r.bo, 0),
    tot_cost:  sumNum(products, r => r.ordered_tot_cost),
  }

  return (
    <div data-testid="products-tab">
      <DataTable
        rows={products}
        columns={columns}
        rowKey={r => r.id}
        emptyText="No products on this VPO"
        filteredEmptyText="No products match your filters"
      />

      {products.length > 0 && (
        <div
          data-testid="products-summary"
          className="mt-3 grid grid-cols-2 gap-2 rounded-lg bg-zinc-50 p-3 text-xs sm:grid-cols-6 dark:bg-zinc-800/50"
        >
          <SummaryCell label="Ordered"   value={totals.ordered} />
          <SummaryCell label="Confirmed" value={totals.confirmed} />
          <SummaryCell label="Invoiced"  value={totals.invoiced} />
          <SummaryCell label="Received"  value={totals.received} />
          <SummaryCell label="BO"        value={totals.bo} />
          <SummaryCell label="Total Cost" value={money(totals.tot_cost, currency)} />
        </div>
      )}
    </div>
  )
}

function SummaryCell({ label, value }: { label: string; value: number | string }) {
  return (
    <div>
      <div className="text-zinc-500 dark:text-zinc-400">{label}</div>
      <div className="font-semibold tabular-nums text-zinc-900 dark:text-white">{value}</div>
    </div>
  )
}
